import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Form';
import { Plant, ArrowRight } from '@phosphor-icons/react';
import BrandFooter from '@/components/BrandFooter';

const HERO = 'https://images.unsplash.com/photo-1717292741426-d050f4f25503?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtYW5ncm92ZSUyMHRyZWUlMjByb290c3xlbnwwfHx8fDE3NzkxNDc2ODJ8MA&ixlib=rb-4.1.0&q=85';

export default function Login() {
  const { user, login, register, loading } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('inspector@mangroveguard.io');
  const [password, setPassword] = useState('password123');
  const [name, setName] = useState('Field Inspector');
  const [err, setErr] = useState('');

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      if (mode === 'login') await login(email, password);
      else await register(name, email, password);
      nav('/');
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col max-w-md mx-auto">
      <div className="relative h-48 border-b-2 border-carbon overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-carbon/40" />
        <div className="absolute inset-0 p-5 flex flex-col justify-end text-canvas">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-brand-primary border-2 border-canvas flex items-center justify-center">
              <Plant size={18} weight="bold" />
            </div>
            <span className="font-display font-black text-xl tracking-tighter">MANGROVEGUARD</span>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest opacity-90">
            Blue Carbon · dMRV · Field Inspector
          </p>
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <h1 className="font-display text-3xl font-black tracking-tighter leading-none mb-1">
          {mode === 'login' ? 'Sign in to your kit' : 'Create inspector account'}
        </h1>
        <p className="text-carbon-soft text-sm mb-6">
          Offline-ready field data collection for mangrove carbon projects.
        </p>

        <form onSubmit={submit} className="space-y-4 flex-1">
          {mode === 'register' && (
            <div>
              <Label>Full name</Label>
              <Input
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
              />
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.org"
              required
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>

          {err && (
            <div data-testid="auth-error" className="border-2 border-status-severe bg-red-50 px-3 py-2 text-sm text-status-severe font-medium">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid={mode === 'login' ? 'login-submit-btn' : 'register-submit-btn'}
            className="w-full"
            size="lg"
          >
            {loading ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
            <ArrowRight size={20} weight="bold" />
          </Button>

          <button
            type="button"
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            data-testid="toggle-auth-mode"
            className="w-full text-sm font-display uppercase tracking-widest font-bold py-3 hover:underline"
          >
            {mode === 'login' ? 'New inspector? Create an account →' : '← Back to sign in'}
          </button>
        </form>
      </div>
      <BrandFooter />
    </div>
  );
}
